import { useLocation } from "@solidjs/router";
import {
    KeyboardArrowDownRounded,
} from "@suid/icons-material";
import QrCodeScannerIcon from "@suid/icons-material/QrCodeScanner";
import {
    Alert,
    ButtonGroup,
    Menu,
    MenuItem,
    useTheme,
} from "@suid/material";
import { t } from "i18next";
import {
    type Component,
    createEffect,
    createSignal,
    For,
    lazy,
    Match,
    Show,
    Switch,
} from "solid-js";
import { useAuth } from "src/hooks/useAuth";
import { Redirect } from "../challenges/Redirect";
import {
    Grid,
    Tooltip,
} from "../components";
import { translateError } from "./Error";
import { Button } from "../components/Button";
import { CollapseButton } from "../components/CollapseButton";
import { LoginDialog } from "../components/LoginDialog";
import { Snackbar } from "../components/Snackbar/index.js";
import { config } from "../config";
import { ErrorSeverity } from "../domain/index.js";

const QrScannerDialog = lazy(async () => import("../components/QrScannerDialog/QrScannerDialog"));

enum LogoutState {
    HERE,
    OTHER,
    ALL,
}

export const Apps: Component = () => {
    const location = useLocation();
    let redirectUrl: URL | undefined;
    if (location.pathname === "/apps") {
        const url = new URL(window.location.href);
        const expectedUrl = new URL(config.app.url ?? config.baseApi);
        if (expectedUrl.host !== url.host) {
            redirectUrl = expectedUrl;
        }
    }
    if (redirectUrl) {
        return <Redirect url={redirectUrl?.toString() ?? ""} />;
    }

    const theme = useTheme();
    const auth = useAuth();
    const [qrScannerEnabled, setQrScannerEnabled] = createSignal(false);

    const [showErrors, setShowErrors] = createSignal(false);
    createEffect(() => {
        if (auth.errors().length > 0) {
            setShowErrors(true);
        }
    });
    const hideErrors = (): void => {
        setShowErrors(false);
        auth.clearErrors();
    };

    const [dropDownAnchor, setDropDownAnchor] = createSignal<null | HTMLElement>(null);
    const handleDropDownClose = async (value?: LogoutState): Promise<void> => {
        if (value === LogoutState.OTHER || value === LogoutState.ALL) {
            await auth.logoutOther();
        }
        if (value === LogoutState.HERE || value === LogoutState.ALL) {
            await auth.logout();
        }
        setDropDownAnchor(null);
    };

    return (
        <>
            <Switch>
                <Match when={!auth.isAuthenticated()}>
                    <LoginDialog
                        id="ProwiseAppsPage"
                        title={t("overview.title.signing_in")}
                        showLanguageSelect={false}
                        primaryButton={<Button
                            data-testid="LoginButton"
                            color="secondary"
                            variant="contained"
                            disabled={auth.isLoading()}
                            onClick={() => void auth.login(true)}
                            sx={theme.mixins.button}
                        >
                            {t("general.btn.sign_in")}
                        </Button>}
                        errors={[]}
                    />
                </Match>
                <Match when={true}>
                    <LoginDialog
                        minContentWidth
                        id="ProwiseAppsPage"
                        title={t("overview.title.hello_{name}_{email}", { name: auth.getUser()?.profile.name, email: auth.getUser()?.profile.email })}
                        description={t("overview.p.where_to_go_{name}_{email}", { name: auth.getUser()?.profile.name, email: auth.getUser()?.profile.email })}
                        showLanguageSelect={false}
                        secondaryButton={<>
                            <ButtonGroup id="logoutGroup" variant="outlined" color="secondary" sx={theme.mixins.buttonGroup} aria-label="Basic button group">
                                <Button
                                    data-testid="LogoutButton"
                                    onClick={() => void handleDropDownClose(LogoutState.HERE)}
                                    sx={theme.mixins.buttonOutlined}
                                >
                                    {t("general.btn.sign_out")}
                                </Button>
                                <Show when={typeof auth.logoutOtherSessionsEndpoint.latest === "string"}>
                                    <Button
                                        data-testid="LogoutOptionButton"
                                        sx={{
                                            ...theme.mixins.buttonOutlined,
                                            padding: "5px 10px",
                                            minWidth: "unset",
                                        }}
                                        onClick={() => {
                                            setDropDownAnchor(document.querySelector<HTMLElement>("#logoutGroup"));
                                        }}
                                    >
                                        <KeyboardArrowDownRounded />
                                    </Button>
                                </Show>
                            </ButtonGroup>
                            <Menu
                                data-testid="LogoutOptionsMenu"
                                anchorEl={dropDownAnchor()}
                                open={dropDownAnchor() !== null}
                                onClose={() => void handleDropDownClose()}
                                PaperProps={{ elevation: 3, sx: theme.mixins.menuPaper }}
                            >
                                <For each={[
                                // [LogoutState.HERE, t("general.btn.sign_out_here")],
                                    [LogoutState.OTHER, t("general.btn.sign_out_other")],
                                    [LogoutState.ALL, t("general.btn.sign_out_all")],
                                ]}
                                >
                                    {(item) => (
                                        <MenuItem
                                            sx={theme.mixins.menuItem}
                                            onClick={() => void handleDropDownClose(item[0])}
                                        >
                                            {item[1]}
                                        </MenuItem>
                                    )}
                                </For>
                            </Menu>
                        </>}
                        primaryButton={
                            <Tooltip title={t("general.info.qr_scanner_button")}>
                                <CollapseButton
                                    data-testid="QrButton"
                                    color="secondary"
                                    variant="outlined"
                                    onClick={() => setQrScannerEnabled(true)}
                                    sx={theme.mixins.buttonOutlined}
                                    startIcon={<QrCodeScannerIcon />}
                                >
                                    {t("general.btn.open_qr_scanner")}
                                </CollapseButton>
                            </Tooltip>
                        }
                        errors={[]}
                    >
                        <Grid
                            items={auth.getAppsCategorized()}
                            desktop={4}
                            mobile={4}
                            landscape={4}
                            tileSize="91px" // 75px icon + 16px padding
                            round
                            showLabels
                            // @ts-expect-error - Single select item has meta
                            onSelection={({ meta }) => {
                                window.location.href = meta.url;
                            }}
                        />
                    </LoginDialog>

                    <QrScannerDialog
                        title={t("general.title.qr_scanner")}
                        open={qrScannerEnabled()}
                        onClose={() => setQrScannerEnabled(false)}
                    />
                    <Show when={showErrors()}>
                        <Snackbar open={true} onClose={hideErrors} autoHideDuration={6000}>
                            <For each={auth.errors()}>
                                {(error) => {
                                    if (error.severity === ErrorSeverity.NONE) {
                                        return (
                                            <Alert severity="success" elevation={5} sx={{ my: 1 }}>
                                                {t("sign_out.title.success")}
                                            </Alert>
                                        );
                                    }
                                    return (
                                        <Alert severity="error" elevation={5} sx={{ my: 1 }}>
                                            {translateError(error.id, true) || error.message}
                                        </Alert>
                                    );
                                }}
                            </For>
                        </Snackbar>
                    </Show>
                </Match>
            </Switch>
        </>
    );
};

export default Apps;
